import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdateFaqAPI } from "Api/faqAPI";
import PropTypes from "prop-types";

const CreateOrUpdateFAQModal = ({ isOpen, onClose, onSubmit, isEditMode }) => {
  const { faqFormData, setfaqFormData, initialFaqFormState, setFetchFaq } =
    useGlobalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDataEdited, setIsDataEdited] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsDataEdited(false);
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setfaqFormData({
      ...faqFormData,
      [name]: value,
    });

    if (name === "key") {
      setErrorMessage("");
    }

    const isEdited =
      faqFormData?.key !== initialFaqFormState?.key ||
      faqFormData?.title !== initialFaqFormState?.title ||
      faqFormData?.description !== initialFaqFormState?.description;
    setIsDataEdited(isEdited);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");

    const response = await createOrUpdateFaqAPI(
      faqFormData,
      setIsSubmitting,
      token
    );

    if (response?.error) {
      if (response.message === "sortOrder already exists") {
        toast.error("Sort order already exists");
      } else {
        toast.error(
          response.message || "Error creating/updating FAQ. Please try again."
        );
      }
      return;
    }

    const successMessage = faqFormData?.id
      ? "FAQ updated successfully"
      : "FAQ created successfully";
    toast.success(successMessage);
    onSubmit(response.data);
    onClose();
    setfaqFormData(initialFaqFormState);
    setFetchFaq(true);
  };

  const handleCancel = () => {
    setfaqFormData(initialFaqFormState);
    onClose();
  };

  const isFormValid = useCallback(() => {
    return (
      faqFormData?.key?.trim() &&
      faqFormData?.title.trim() &&
      faqFormData?.description.trim()
    );
  }, [faqFormData]);

  const getButtonLabel = () => {
    if (isSubmitting) return "Submitting…";
    return isEditMode ? "Update" : "Add";
  };

  if (!isOpen) return null;

  const submitButtonClass =
    isSubmitting || !isFormValid() || !isDataEdited
      ? "cursor-not-allowed bg-gray-400"
      : "bg-green-600 hover:bg-green-500";

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center bg-opacity-50">
      <div className="relative z-50 mx-auto flex w-4/5 flex-col items-center justify-start rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 dark:text-white">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
        >
          <IoMdClose size={24} />
        </button>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label
                htmlFor="key"
                className="mb-2 block font-bold text-gray-700 dark:text-white"
              >
                <span className="text-red-600">*</span> Key:
              </label>
              <input
                id="key"
                type="text"
                name="key"
               autoComplete="off"
                value={faqFormData?.key}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
            <div className="w-1/2">
              <label
                htmlFor="title"
                className="mb-2 block font-bold text-gray-700 dark:text-white"
              >
                <span className="text-red-600">*</span> Title:
              </label>
              <input
                id="title"
                type="text"
                name="title"
                value={faqFormData?.title}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>

            <div className="w-1/2">
              <label
                htmlFor="sortOrder"
                className="mb-2 block font-bold text-gray-700 dark:text-white"
              >
                <span className="text-red-600">*</span> Sort number:
              </label>
              <input
                id="sortOrder"
                type="number"
                min="0"
                name="sortOrder"
                value={faqFormData?.sortOrder}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>
          <div className="mt-4 mb-4 flex justify-start gap-4">
            <div className="w-full">
              <label
                htmlFor="description"
                className="mb-2 block font-bold text-gray-700  dark:text-white"
              >
                <span className="text-red-600">*</span> Description:
              </label>
              <textarea
                id="description"
                name="description"
                value={faqFormData?.description}
                onChange={handleChange}
                className="focus:border-transparent h-40 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              ></textarea>
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>
          <div className="mb-8 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${submitButtonClass}`}
              disabled={isSubmitting || !isFormValid() || !isDataEdited}
            >
              {getButtonLabel()}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CreateOrUpdateFAQModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

export default CreateOrUpdateFAQModal;
